/**
 * features/achievements/AchievementsModule.tsx
 * ------------------------------------------------------------------
 * Achievements — the positive counterpart to Discipline. Structured
 * commendations, awards and milestones per student (category, level,
 * title, description, award date, optional merit points). Surfaced to
 * parents via getChildAcademics, so the portal celebrates students, not
 * only flags problems.
 *
 * Permission: `canManageAchievements` (mirrors canManageDiscipline —
 * admin, headteacher, hoy, hod, class/subject teachers, contextually
 * scoped to their own classes by the UI). Stored per-record in
 * `reportAchievements`.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, deleteDoc, doc, getDocs, query, orderBy,
} from 'firebase/firestore';
import {
  Award, Plus, Search, Pencil, Trash2, Save, ChevronLeft, AlertCircle,
  CheckCircle2, Calendar, Star, Trophy, Medal, X,
} from 'lucide-react';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getLinkedTeacher } from '@/lib/permissions';
import { sanitizeComment, stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import StudentAvatar from '@/components/ui/StudentAvatar';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/components/ui/glass';
import type { AchievementRecord, AchievementCategory, AchievementLevel } from '@/types';

const CATEGORIES: { value: AchievementCategory; label: string }[] = [
  { value: 'academic', label: 'Academic' },
  { value: 'sport', label: 'Sport' },
  { value: 'arts', label: 'Arts & Culture' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'citizenship', label: 'Citizenship' },
  { value: 'improvement', label: 'Most Improved' },
  { value: 'service', label: 'Service' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'other', label: 'Other' },
];

const LEVELS: { value: AchievementLevel; label: string; color: string }[] = [
  { value: 'class', label: 'Class', color: 'bg-slate-50 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300' },
  { value: 'school', label: 'School', color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'zonal', label: 'Zonal', color: 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  { value: 'regional', label: 'Regional', color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  { value: 'national', label: 'National', color: 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'international', label: 'International', color: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' },
];

const categoryLabel = (c: AchievementCategory) => CATEGORIES.find((x) => x.value === c)?.label ?? c;
const levelInfo = (l: AchievementLevel) => LEVELS.find((x) => x.value === l) ?? LEVELS[0];

interface FormState {
  studentId: string;
  category: AchievementCategory;
  level: AchievementLevel;
  title: string;
  description: string;
  awardDate: string;
  points: string;
  parentNotified: boolean;
}
const EMPTY_FORM: FormState = {
  studentId: '', category: 'academic', level: 'school', title: '',
  description: '', awardDate: new Date().toISOString().slice(0, 10), points: '', parentNotified: false,
};

export default function AchievementsModule() {
  const { students, classes, teachers, loading: dataLoading } = useData();
  const { userData, userRole } = useAuth();

  const [records, setRecords] = useState<AchievementRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [mode, setMode] = useState<'list' | 'form'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  const PRIVILEGED = ['admin', 'headteacher'];
  const isPrivileged = PRIVILEGED.includes(userRole ?? '');

  const myClasses = useMemo(() => {
    if (isPrivileged) return classes;
    const t = getLinkedTeacher(userData, teachers);
    if (!t) return classes; // fall back to all (subject teachers span classes)
    return classes;
  }, [classes, userData, teachers, isPrivileged]);

  const loadRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const snap = await getDocs(query(collection(db, COLLECTIONS.achievements), orderBy('createdAt', 'desc')));
      setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() } as AchievementRecord)));
    } catch (e) {
      console.error('Failed to load achievements:', e);
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => { if (!dataLoading) loadRecords(); }, [dataLoading, loadRecords]);

  const filteredRecords = useMemo(() => {
    const q = search.toLowerCase();
    return records.filter((r) => {
      if (classFilter && r.classId !== classFilter) return false;
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (levelFilter && r.level !== levelFilter) return false;
      if (q && !r.studentName.toLowerCase().includes(q) && !(r.title || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [records, search, classFilter, categoryFilter, levelFilter]);

  const stats = useMemo(() => {
    const thisMonth = records.filter((r) => (r.awardDate || r.createdAt)?.startsWith(new Date().toISOString().slice(0, 7))).length;
    const topLevel = records.filter((r) => ['regional', 'national', 'international'].includes(r.level)).length;
    const students = new Set(records.map((r) => r.studentId)).size;
    return { total: records.length, thisMonth, topLevel, students };
  }, [records]);

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? id;
  const studentObj = (id: string) => students.find((s) => s.id === id);

  function openNew() { setEditingId(null); setForm({ ...EMPTY_FORM }); setMsg(null); setMode('form'); }
  function openEdit(r: AchievementRecord) {
    setEditingId(r.id);
    setForm({
      studentId: r.studentId, category: r.category, level: r.level, title: r.title,
      description: r.description ?? '', awardDate: r.awardDate, points: r.points != null ? String(r.points) : '',
      parentNotified: r.parentNotified ?? false,
    });
    setMsg(null); setMode('form');
  }

  async function handleSave() {
    const errors: string[] = [];
    if (!form.studentId) errors.push('Select a student.');
    if (!form.title.trim()) errors.push('A title is required.');
    if (!form.awardDate) errors.push('Award date is required.');
    if (errors.length) { setMsg({ ok: false, text: errors.join(' ') }); return; }

    const student = students.find((s) => s.id === form.studentId);
    if (!student) { setMsg({ ok: false, text: 'Student not found.' }); return; }

    setSaving(true); setMsg(null);
    try {
      const now = new Date().toISOString();
      const actor = userData?.uid ?? 'system';
      const actorName = userData?.displayName ?? 'System';
      const cls = classes.find((c) => c.id === student.currentClass);
      const pts = form.points.trim() === '' ? undefined : Math.max(0, parseInt(form.points, 10) || 0);
      const payload = stripUndefined({
        studentId: student.id,
        studentName: student.fullName,
        classId: student.currentClass,
        className: cls?.name ?? student.currentClass,
        category: form.category,
        level: form.level,
        title: sanitizeComment(form.title, 200),
        description: sanitizeComment(form.description, 2000) || undefined,
        awardDate: form.awardDate,
        points: pts,
        parentNotified: form.parentNotified,
        updatedAt: now,
        updatedBy: actor,
      });
      if (editingId) {
        await updateDoc(doc(db, COLLECTIONS.achievements, editingId), payload);
        setSaveModal({ show: true, status: 'success', message: 'Achievement updated.' });
        auditFromContext(userData, 'achievement_updated', 'academic', { targetId: editingId, targetType: 'achievement', targetName: student.fullName, details: { category: form.category, level: form.level } });
      } else {
        await addDoc(collection(db, COLLECTIONS.achievements), stripUndefined({ ...payload, recordedBy: actor, recordedByName: actorName, createdAt: now }));
        setSaveModal({ show: true, status: 'success', message: 'Achievement recorded.' });
        auditFromContext(userData, 'achievement_created', 'academic', { targetType: 'achievement', targetName: student.fullName, details: { category: form.category, level: form.level } });
        setForm({ ...EMPTY_FORM });
      }
      await loadRecords();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setSaving(false); }
  }

  async function handleDelete(id: string) {
    try {
      const rec = records.find((r) => r.id === id);
      await deleteDoc(doc(db, COLLECTIONS.achievements, id));
      auditFromContext(userData, 'achievement_deleted', 'academic', { targetId: id, targetType: 'achievement', targetName: rec?.studentName });
      setRecords((rs) => rs.filter((r) => r.id !== id));
      setConfirmDelete(null);
      setMsg({ ok: true, text: 'Achievement deleted.' });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  if (dataLoading) return <div className="flex items-center justify-center min-h-[400px]"><Spinner label="Loading…" /></div>;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Award}
        title="Achievements"
        subtitle={mode === 'form' ? (editingId ? 'Edit achievement' : 'Record an achievement') : `${stats.total} recorded · ${stats.students} students celebrated`}
        actions={mode === 'list'
          ? <AccentButton icon={Plus} onClick={openNew}>Record Achievement</AccentButton>
          : <AccentButton icon={ChevronLeft} variant="soft" onClick={() => { setMode('list'); setMsg(null); }}>Back to List</AccentButton>}
      />
      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}{msg.text}
          </div>
        </SoftCard>
      )}
      {mode === 'list' ? renderList() : renderForm()}
      <SaveConfirmation show={saveModal.show} status={saveModal.status} message={saveModal.message} onDismiss={() => setSaveModal({ ...saveModal, show: false })} />
    </div>
  );

  function renderList() {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatChip tone="gold" value={stats.total} label="Total Awards" icon={Award} />
          <StatChip tone="emerald" value={stats.students} label="Students" icon={Star} />
          <StatChip tone="blue" value={stats.topLevel} label="Regional+" icon={Trophy} />
          <StatChip tone="gold" value={stats.thisMonth} label="This Month" icon={Calendar} />
        </div>
        <SoftCard>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="text" placeholder="Search student or award…" className={glassFieldClass('pl-10')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className={glassFieldClass('sm:w-40')} value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">All Classes</option>
              {myClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className={glassFieldClass('sm:w-40')} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select className={glassFieldClass('sm:w-36')} value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              <option value="">All Levels</option>
              {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
        </SoftCard>

        {loadingRecords ? (
          <Spinner label="Loading achievements…" />
        ) : filteredRecords.length === 0 ? (
          <SoftCard><p className="text-sm text-gray-500 text-center py-10">No achievements yet — record one to start celebrating your students.</p></SoftCard>
        ) : (
          <div className="space-y-2.5">
            {filteredRecords.map((r) => {
              const li = levelInfo(r.level);
              const s = studentObj(r.studentId);
              return (
                <SoftCard key={r.id} pad="p-4">
                  <div className="flex items-start gap-3">
                    {s ? <StudentAvatar student={s} size="md" className="mt-0.5" /> : <div className="w-12 h-12 rounded-xl bg-primary/10 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 dark:text-white">{r.studentName}</span>
                        <span className="text-xs text-gray-400">{className(r.classId)}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${li.color}`}>{li.label}</span>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{categoryLabel(r.category)}</span>
                        {typeof r.points === 'number' && <span className="text-[11px] font-semibold text-amber-600 inline-flex items-center gap-0.5"><Medal size={12} />{r.points} pts</span>}
                      </div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mt-1">{r.title}</p>
                      {r.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{r.description}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">{r.awardDate} · by {r.recordedByName}{r.parentNotified ? ' · parent notified' : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(r)} className="p-2 text-gray-400 hover:text-primary" title="Edit"><Pencil size={15} /></button>
                      {confirmDelete === r.id ? (
                        <span className="flex items-center gap-1">
                          <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 text-xs font-semibold">Delete</button>
                          <button onClick={() => setConfirmDelete(null)} className="p-1.5 text-gray-400"><X size={14} /></button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmDelete(r.id)} className="p-2 text-gray-400 hover:text-red-500" title="Delete"><Trash2 size={15} /></button>
                      )}
                    </div>
                  </div>
                </SoftCard>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderForm() {
    return (
      <SoftCard pad="p-5 sm:p-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-500">Student</label>
            <select className={glassFieldClass('mt-1')} value={form.studentId} onChange={(e) => setForm({ ...form, studentId: e.target.value })}>
              <option value="">— select student —</option>
              {[...students].sort((a, b) => a.fullName.localeCompare(b.fullName)).map((s) => (
                <option key={s.id} value={s.id}>{s.fullName} · {className(s.currentClass)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Category</label>
            <select className={glassFieldClass('mt-1')} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as AchievementCategory })}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Level</label>
            <select className={glassFieldClass('mt-1')} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value as AchievementLevel })}>
              {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-500">Title</label>
            <input className={glassFieldClass('mt-1')} placeholder="e.g. 1st place — Regional Maths Olympiad" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-500">Description (optional)</label>
            <textarea rows={3} className={glassFieldClass('mt-1')} placeholder="Details of the achievement…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Award date</label>
            <input type="date" className={glassFieldClass('mt-1')} value={form.awardDate} onChange={(e) => setForm({ ...form, awardDate: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Merit points (optional)</label>
            <input type="number" min={0} className={glassFieldClass('mt-1')} placeholder="e.g. 10" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} />
          </div>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={form.parentNotified} onChange={(e) => setForm({ ...form, parentNotified: e.target.checked })} className="rounded" />
            Parent notified
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <AccentButton variant="soft" onClick={() => { setMode('list'); setMsg(null); }}>Cancel</AccentButton>
          <AccentButton icon={Save} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editingId ? 'Update' : 'Record Achievement'}</AccentButton>
        </div>
      </SoftCard>
    );
  }
}
